import React, { useRef, useState, useEffect } from 'react';
import { Box, HStack, Text, VStack, useMediaQuery, Badge, Pressable, Tooltip } from 'native-base';
import { BarChart3, Sparkles as SparklesIcon, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../utils/fonts';
import { COLORS } from '../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../utils/screens';
import NoDataFound from './noDataFound';

interface QuotationAnalysisDashboardV2Props {
  data: any;
  eventDetails?: any;
}

const QuotationAnalysisDashboardV2: React.FC<QuotationAnalysisDashboardV2Props> = ({ data, eventDetails }) => {
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });

  // Extract the actual analysis data - V2 API response structure
  const analysisData = data?.data?.data || data?.data || data;

  if (!analysisData || !analysisData.items) {
    return (
      <NoDataFound dataName="analysis data" />
    );
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getVendorName = (vendorId: string) => {
    const vendor = analysisData.vendors?.find((v: any) => v.id === vendorId);
    return vendor?.name || 'Unknown Vendor';
  };

  // Helper component to show tooltip only when text is truncated
  const TruncatedTextTooltip: React.FC<{ text: string; children: React.ReactElement }> = ({ text, children }) => {
    const textRef = useRef<any>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (textRef.current) {
          const element = textRef.current;
          // Check if text is actually truncated
          const truncated = element.scrollWidth > element.clientWidth;
          setIsTruncated(truncated);
        }
      };
      // Use setTimeout to ensure DOM is fully rendered
      const timeoutId = setTimeout(checkTruncation, 0);
      window.addEventListener('resize', checkTruncation);
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', checkTruncation);
      };
    }, [text]);

    const clonedChild = React.cloneElement(children, { ref: textRef });

    if (isTruncated) {
      return (
        <Tooltip label={text}>
          <Pressable>{clonedChild}</Pressable>
        </Tooltip>
      );
    }

    return clonedChild;
  };

  // Calculate vendor wins across all items
  const calculateVendorWins = () => {
    const wins: Record<string, number> = {};
    
    analysisData.items?.forEach((head: any) => {
      head.sub_heads?.forEach((subHead: any) => {
        subHead.items?.forEach((item: any) => {
          const vendorQuotes = item.vendorQuotes || [];
          vendorQuotes.forEach((quote: any) => {
            if (quote.isBestPrice) {
              wins[quote.vendorId] = (wins[quote.vendorId] || 0) + 1;
            }
          });
        });
      });
    });
    
    return wins;
  };

  // Format event dates
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Get event status and badge color
  const getEventStatus = () => {
    if (!eventDetails?.status) return null;
    
    const statusMap: Record<string, { label: string; color: string; bgColor: string }> = {
      'event_request': { label: 'Request', color: '#F59E0B', bgColor: '#FEF3C7' }, // Orange/Amber with light bg
      'event_upcoming': { label: 'Upcoming', color: '#3B82F6', bgColor: '#DBEAFE' }, // Blue with light bg
      'event_started': { label: 'Started', color: '#2E7D32', bgColor: '#E8F5E9' }, // Green with light bg
      'event_completed': { label: 'Completed', color: '#6B7280', bgColor: '#F3F4F6' }, // Grey with light bg
    };
    
    return statusMap[eventDetails.status] || null;
  };

  // Parse AI summary to extract insights with random color coding
  const parseAISummary = (summary: string) => {
    if (!summary) return [];
    
    const insights: Array<{ text: string; color: string }> = [];
    // 5 predefined colors for sparkle icons
    const sparkleColors = ['#888', '#16A34A', '#F59E0B', '#DC2626', '#3B82F6']; // Grey, Green, Orange, Red, Blue
    
    // Split by lines and process
    const lines = summary.split('\n').filter(line => line.trim());
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Skip empty lines and headers
      if (!trimmed || trimmed.startsWith('#')) return;
      
      // Remove markdown list markers but keep the content
      let cleanLine = trimmed.replace(/^[-•*]\s+/, '').replace(/^\d+\.\s+/, '');
      
      // Skip very short lines
      if (cleanLine.length < 10) return;
      
      // Assign color cyclically from the 5 colors
      const color = sparkleColors[index % sparkleColors.length];
      
      insights.push({ text: cleanLine, color });
    });
    
    return insights;
  };


  return (
    <VStack space={6} width="100%">
      {/* Event Info + Stats Row */}
      <HStack 
        space={4} 
        alignItems="stretch"
        flexWrap={MobileWindow ? "wrap" : "nowrap"}
      >
        {/* Event Card - 60% width on desktop */}
        <Box 
          flex={MobileWindow ? "1 1 100%" : "0 0 59%"}
          bg="white"
          borderRadius="xl"
          borderWidth={1}
          borderColor="#e8e8e8"
          p={MobileWindow ? 4 : 6}
          shadow="sm"
        >
          <VStack space={3}>
            <HStack justifyContent="space-between" alignItems="flex-start">
              <VStack flex={1} space={1}>
                <Text
                  fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  color="#888"
                  textTransform="capitalize"
                  letterSpacing="0.5px"
                  fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                >
                  {eventDetails?.type === 'event' ? 'Event' : 'MICE'} Name
                </Text>
                <Text
                  fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  color={COLORS.BLACK_COLOR}
                >
                  {eventDetails?.event_name}
                </Text>
              </VStack>
              {(() => {
                const status = getEventStatus();
                return status ? (
                  <Badge
                    bg={status.bgColor}
                    borderRadius="full"
                    px={3}
                    py={1}
                  >
                    <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={status.color} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                      {status.label}
                    </Text>
                  </Badge>
                ) : null;
              })()}
            </HStack>
            
            <HStack space={6} mt={3} flexWrap={MobileWindow ? "wrap" : "nowrap"}>
              {eventDetails?.from_date && eventDetails?.to_date && (
                <VStack space={1} flex={1} minW={MobileWindow ? "100%" : "auto"}>
                  <Text 
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} 
                    color="#888" 
                    textTransform="capitalize" 
                    letterSpacing="0.5px"
                    fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                  >
                    Duration
                  </Text>
                  <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color="#444" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                    {formatDate(eventDetails.from_date)} — {formatDate(eventDetails.to_date)}
                  </Text>
                </VStack>
              )}
              
              {eventDetails?.contact_details?.coordinator_name && (
                <VStack space={1} flex={1} minW={MobileWindow ? "100%" : "auto"}>
                  <Text 
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} 
                    color="#888" 
                    textTransform="capitalize" 
                    letterSpacing="0.5px"
                    fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                  >
                    Coordinator Name
                  </Text>
                  <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color="#444" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                    {eventDetails.contact_details.coordinator_name}
                  </Text>
                </VStack>
              )}
              
              {eventDetails?.contact_details?.email && (
                <VStack space={1} flex={1} minW={MobileWindow ? "100%" : "auto"}>
                  <Text 
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} 
                    color="#888" 
                    textTransform="capitalize" 
                    letterSpacing="0.5px"
                    fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                  >
                    Contact Email
                  </Text>
                  <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color={COLORS.THEME_COLOR} fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}>
                    {eventDetails.contact_details.email}
                  </Text>
                </VStack>
              )}
            </HStack>
          </VStack>
        </Box>

        {/* Stats Cards - 4 cards in a row */}
        <HStack 
          flex={MobileWindow ? "1 1 100%" : "0 0 40%"}
          space={3}
          flexWrap={MobileWindow ? "wrap" : "nowrap"}
          justifyContent="space-between"
        >
          {/* Items Card */}
          <Box 
            flex={MobileWindow ? "1 1 48%" : 1}
            bg="white"
            borderRadius="xl"
            borderWidth={1}
            borderColor="#e8e8e8"
            p={MobileWindow ? 4 : 5}
            shadow="sm"
            alignItems="center"
            justifyContent="center"
          >
            <VStack alignItems="center" space={1} width="100%">
              <TruncatedTextTooltip text={String(analysisData.summary?.totalItems || 0)}>
                <Text
                  fontSize="28px"
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  color={COLORS.BLACK_COLOR}
                  textAlign="center"
                  maxWidth="120px"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as any}
                >
                  {analysisData.summary?.totalItems || 0}
                </Text>
              </TruncatedTextTooltip>
              <Text
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                color="#888"
                textTransform="uppercase"
                letterSpacing="0.5px"
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                textAlign="center"
              >
                Items
              </Text>
            </VStack>
          </Box>

          {/* Savings Card */}
          <Box 
            flex={MobileWindow ? "1 1 48%" : 1}
            bg="white"
            borderRadius="xl"
            borderWidth={1}
            borderColor="#e8e8e8"
            p={MobileWindow ? 4 : 5}
            shadow="sm"
            alignItems="center"
            justifyContent="center"
          >
            <VStack alignItems="center" space={1} width="100%">
              <TruncatedTextTooltip text={formatCurrency(analysisData.summary?.vendorSelectionSavings || 0)}>
                <Text
                  fontSize="28px"
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  color="#16A34A"
                  textAlign="center"
                  maxWidth="120px"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as any}
                >
                  {formatCurrency(analysisData.summary?.vendorSelectionSavings || 0)}
                </Text>
              </TruncatedTextTooltip>
              <Text
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                color="#888"
                textTransform="uppercase"
                letterSpacing="0.5px"
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                textAlign="center"
              >
                Savings
              </Text>
            </VStack>
          </Box>

          {/* Above Bench Card */}
          <Box 
            flex={MobileWindow ? "1 1 48%" : 1}
            bg="white"
            borderRadius="xl"
            borderWidth={1}
            borderColor="#e8e8e8"
            p={MobileWindow ? 4 : 5}
            shadow="sm"
            alignItems="center"
            justifyContent="center"
          >
            <VStack alignItems="center" space={1} width="100%">
              <TruncatedTextTooltip text={String(analysisData.summary?.benchmarkComparison?.aboveBenchmark || 0)}>
                <Text
                  fontSize="28px"
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  color="#DC2626"
                  textAlign="center"
                  maxWidth="120px"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as any}
                >
                  {analysisData.summary?.benchmarkComparison?.aboveBenchmark || 0}
                </Text>
              </TruncatedTextTooltip>
              <Text
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                color="#888"
                textTransform="uppercase"
                letterSpacing="0.5px"
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                textAlign="center"
              >
                Above Bench
              </Text>
            </VStack>
          </Box>

          {/* Below Bench Card */}
          <Box 
            flex={MobileWindow ? "1 1 48%" : 1}
            bg="white"
            borderRadius="xl"
            borderWidth={1}
            borderColor="#e8e8e8"
            p={MobileWindow ? 4 : 5}
            shadow="sm"
            alignItems="center"
            justifyContent="center"
          >
            <VStack alignItems="center" space={1} width="100%">
              <TruncatedTextTooltip text={String(analysisData.summary?.benchmarkComparison?.belowBenchmark || 0)}>
                <Text
                  fontSize="28px"
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  color="#16A34A"
                  textAlign="center"
                  maxWidth="120px"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as any}
                >
                  {analysisData.summary?.benchmarkComparison?.belowBenchmark || 0}
                </Text>
              </TruncatedTextTooltip>
              <Text
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} 
                color="#888"
                textTransform="uppercase"
                letterSpacing="0.5px"
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                textAlign="center"
              >
                Below Bench
              </Text>
            </VStack>
          </Box>
        </HStack>
      </HStack>

      {/* AI Analysis - Always Visible */}
      {analysisData.summary?.ai_summary && (() => {
        const insights = parseAISummary(analysisData.summary.ai_summary);
        
        return (
          <Box 
            borderRadius="xl" 
            borderWidth={1} 
            borderColor="#FFE4D6"
            shadow="sm"
            overflow="hidden"
            style={{
              background: 'linear-gradient(to bottom, #FFFBF9, #FFF9F7)'
            } as any}
          >
            <HStack alignItems="center" space={1} mb={4} px={6} pt={6}>
              <Box 
                p={3}
                pl={0} 
                borderRadius="lg"
                shadow="md"
              >
                <SparklesIcon size={24} color={COLORS.THEME_COLOR} />
              </Box>
              <Text 
                fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT} 
                fontSize={COMMONFONTSIZE.LARGE_MEDIUM_FONT_SIZE} 
                color={COLORS.ACCORDION_TITLE_COLOR}
              >
                AI Analysis
              </Text>
            </HStack>
            
            <Box px={6} pb={6}>
              <VStack space={3}>
                {insights.map((insight, index) => (
                  <HStack key={index} alignItems="flex-start" space={2}>
                    <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color={insight.color} mt={0.5}>
                      ✦
                    </Text>
                    <Box flex={1}>
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <Text 
                              fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} 
                              color="#444" 
                              fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                              mb={0}
                            >
                              {children}
                            </Text>
                          ),
                          strong: ({ children }) => (
                            <Text 
                              fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} 
                              color="#444" 
                              fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                            >
                              {children}{' '}
                            </Text>
                          ),
                        }}
                      >
                        {insight.text}
                      </ReactMarkdown>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Box>
        );
      })()}


      {/* Items Analysis - Side-by-side Vendor Comparison */}
      {analysisData.items && analysisData.items.length > 0 && (() => {
        const vendorWins = calculateVendorWins();
        
        // Get all unique vendors from all items
        const allVendors = new Set<string>();
        analysisData.items.forEach((head: any) => {
          head.sub_heads?.forEach((subHead: any) => {
            subHead.items?.forEach((item: any) => {
              item.vendorQuotes?.forEach((quote: any) => {
                allVendors.add(quote.vendorId);
              });
            });
          });
        });
        const vendorIds = Array.from(allVendors);
        
        // Find the highest win count
        const maxWins = Math.max(...Object.values(vendorWins), 0);
        
        // Calculate vendor column width dynamically based on number of vendors
        // For 1 vendor: much wider column (900px), for 2 vendors: 450px each, for 3+ vendors: 350px each
        const vendorWidthPerColumn = vendorIds.length === 1 ? 810 : vendorIds.length === 2 ? 450 : 350;
        const vendorColumnWidth = `${vendorWidthPerColumn}px`;

        const itemDetailsWidth = "800px"; // Reduced from 900px to fit content better

        // Sub-column widths
        const qtyWidth = vendorIds.length === 1 ? "200px" : vendorIds.length === 2 ? "110px" : "80px";
        const priceWidth = vendorIds.length === 1 ? "400px" : vendorIds.length === 2 ? "190px" : "160px";
        const varianceWidth = vendorIds.length === 1 ? "300px" : vendorIds.length === 2 ? "150px" : "110px";

        // Calculate table width: item details + (vendor column width * number of vendors)
        const tableWidth = `${800 + (vendorIds.length * vendorWidthPerColumn)}px`;

        return (
          <Box bg="white" borderRadius="xl" shadow="sm" p={6} borderWidth={1} borderColor="gray.200">
            <HStack alignItems="center" space={3} mb={6}>
              <BarChart3 size={24} color={COLORS.THEME_COLOR} />
              <Text
                fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                fontSize={COMMONFONTSIZE.LARGE_MEDIUM_FONT_SIZE}
                color={COLORS.ACCORDION_TITLE_COLOR}
              >
                Item Comparison
              </Text>
            </HStack>

            <Box 
              overflowX="auto" 
              overflowY="visible"
              width="100%"
              maxW="100%"
              style={{ 
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 #f7fafc',
                WebkitOverflowScrolling: 'touch'
              } as any}
            >
              {/* Table Container */}
              <Box 
                bg="white" 
                borderRadius="lg" 
                borderWidth={1} 
                borderColor="#e8e8e8" 
                borderStyle="solid"
                shadow="sm"
                width="max-content"
                overflow="hidden"
              >
                {/* Table Header */}
                <VStack borderBottomWidth={1} borderBottomColor="#e8e8e8" display={MobileWindow ? "none" : "flex"} width="100%">
                  {/* Top Header Row */}
                  <Box 
                    borderBottomWidth={1} 
                    borderBottomColor="#e8e8e8"
                    width="100%"
                    bg="gray.50"
                  >
                    <HStack 
                      width={tableWidth}
                      space={0}
                      alignItems="stretch"
                    >
                      {/* ITEM DETAILS Section - spans all sub-columns */}
                      <Box width={itemDetailsWidth} px={5} py={4} borderRightWidth={1} borderRightColor="#e8e8e8" display="flex" alignItems="center">
                        <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                          Item Details
                        </Text>
                      </Box>
                      
                      {/* Vendor Headers - each spans PRICE and VARIANCE */}
                      {vendorIds.map((vendorId, vendorIndex) => (
                        <Box
                          key={vendorId}
                          width={vendorColumnWidth}
                          borderLeftWidth={vendorIndex > 0 ? 1 : 0}
                          borderLeftColor="#e8e8e8"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <HStack 
                            px={4}
                            py={4}
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            flexWrap="wrap"
                          >
                            <HStack alignItems="center" space={2} flex={1} flexWrap="wrap" justifyContent="center">
                              <TruncatedTextTooltip text={getVendorName(vendorId)}>
                                <Text 
                                  fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} 
                                  color="gray.700" 
                                  textTransform="uppercase" 
                                  letterSpacing="wide" 
                                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                                  maxWidth="200px"
                                  textAlign="center"
                                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as any}
                                >
                                  {getVendorName(vendorId)}
                                </Text>
                              </TruncatedTextTooltip>
                              <Badge
                                bg={vendorWins[vendorId] === maxWins && maxWins > 0 ? "#16A34A" : "#E5E5E5"}
                                borderRadius="full"
                                px={2}
                                py={1}
                              >
                                <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={vendorWins[vendorId] === maxWins && maxWins > 0 ? "white" : "#444"} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                                  {vendorWins[vendorId] || 0} WINS
                                </Text>
                              </Badge>
                            </HStack>
                          </HStack>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                  
                  {/* Sub Header Row */}
                  <HStack 
                    width={tableWidth}
                    space={0}
                    alignItems="stretch"
                    bg="gray.50"
                  >
                    {/* ITEM DETAILS Sub-columns */}
                    <HStack width={itemDetailsWidth} space={0} px={5} py={4} borderRightWidth={1} borderRightColor="#e8e8e8" alignItems="stretch">
                      {/* Category Column */}
                      <Box width="230px" display="flex" alignItems="flex-start" py={1}>
                        <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                          Category
                        </Text>
                      </Box>
                      
                      {/* Description Column */}
                      <Box width="250px" display="flex" alignItems="flex-start" py={1}>
                        <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                          Description
                        </Text>
                      </Box>
                      
                      {/* Unit Column */}
                      <Box width="120px" display="flex" alignItems="center" py={1}>
                        <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                          Unit
                        </Text>
                      </Box>
                      
                      {/* Quantity Column */}
                      {/* <Box width="80px" alignItems="center" display="flex" justifyContent="center" py={1}>
                        <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                          Quantity
                        </Text>
                      </Box> */}
                      
                      {/* Benchmark Column */}
                      <Box width="150px" alignItems="flex-end" display="flex" justifyContent="flex-end" py={1}>
                        <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                          Benchmark
                        </Text>
                      </Box>
                    </HStack>
                    
                    {/* Vendor Sub-columns */}
                    {vendorIds.map((vendorId, vendorIndex) => {
                      return (
                        <HStack 
                          key={vendorId} 
                          width={vendorColumnWidth} 
                          space={0} 
                          borderLeftWidth={vendorIndex > 0 ? 1 : 0} 
                          borderLeftColor="#e8e8e8"
                          alignItems="stretch"
                        >
                          <Box width={qtyWidth} alignItems="center" px={4} py={4} display="flex" justifyContent="center">
                            <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                              Quantity
                            </Text>
                          </Box>
                          <Box width={priceWidth} alignItems="center" px={4} py={4} display="flex" justifyContent="center">
                            <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                              Price
                            </Text>
                          </Box>
                          <Box width={varianceWidth} alignItems="center" px={4} py={4} display="flex" justifyContent="center">
                            <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="#888" textTransform="capitalize" letterSpacing="0.5px" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                              Variance
                            </Text>
                          </Box>
                        </HStack>
                      );
                    })}
                  </HStack>
                </VStack>

                {/* Table Body */}
                <VStack space={0} width="100%">
                  {analysisData.items.map((head: any, headIndex: number) => (
                    head.sub_heads && head.sub_heads.map((subHead: any, subHeadIndex: number) => (
                      subHead.items && subHead.items.map((item: any, itemIndex: number) => {
                        const vendorQuotes = item.vendorQuotes || [];
                        const isLastItem = itemIndex === subHead.items.length - 1 && 
                                          subHeadIndex === head.sub_heads.length - 1 && 
                                          headIndex === analysisData.items.length - 1;
                        
                        return (
                          <HStack
                            key={`${headIndex}-${subHeadIndex}-${itemIndex}`}
                            bg={"white"}
                            borderBottomWidth={isLastItem ? 0 : 1}
                            borderBottomColor="#e8e8e8"
                            width={tableWidth}
                            alignItems="stretch"
                            display={MobileWindow ? "none" : "flex"}
                            space={0}
                          >
                            {/* ITEM DETAILS Section */}
                            <HStack width={itemDetailsWidth} space={0} px={5} py={5} borderRightWidth={1} borderRightColor="#e8e8e8" alignItems="stretch">
                              {/* Category with Sub Head */}
                              <VStack width="230px" space={2} alignItems="flex-start" pr={2} justifyContent="center" py={1}>
                                <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="gray.800" fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>
                                  {head.head_name || '-'}
                                </Text>
                                <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="gray.600">
                                  {subHead.sub_head_name || '-'}
                                </Text>
                              </VStack>
                              
                              {/* Description */}
                              <Box width="250px" px={2} py={1} justifyContent="center" display="flex" alignItems="flex-start">
                                <VStack width="100%" alignItems="flex-start" space={2}>
                                  <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="gray.700" fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                                    {item.name || '-'}
                                  </Text>
                                  <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color="gray.900">
                                    Tracking ID :{' '}
                                    <Text color="gray.900" fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>
                                      {item.trackingId || '-'}
                                    </Text>
                                  </Text>
                                </VStack>
                              </Box>
                              
                              {/* Unit */}
                              <Box width="120px" px={2} py={1} justifyContent="center" display="flex" alignItems="center">
                                <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color={item.description ? "gray.600" : "#d0d0d0"}>
                                  {item.description || '—'}
                                </Text>
                              </Box>
                              
                              {/* Quantity */}
                              {/* <Box width="80px" alignItems="center" justifyContent="center" px={2} py={1} display="flex">
                                <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color="gray.800" fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}>
                                  {item.quantity || 0}
                                </Text>
                              </Box> */}
                              
                              {/* Benchmark */}
                              <Box width="150px" alignItems="flex-end" justifyContent="center" px={2} py={1} display="flex">
                                <Text fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE} color="gray.700" fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}>
                                  {formatCurrency(item.benchmark || 0)}
                                </Text>
                              </Box>
                            </HStack>
                            
                            {/* Vendor Columns */}
                            {vendorIds.map((vendorId, vendorIndex) => {
                              const quote = vendorQuotes.find((q: any) => q.vendorId === vendorId);
                              const isBestPrice = quote?.isBestPrice || false;
                              const displayQty = quote?.quantity || item.quantity || 0;

                              return (
                                <HStack
                                  key={vendorId}
                                  width={vendorColumnWidth}
                                  space={0}
                                  borderLeftWidth={vendorIndex > 0 ? 1 : 0}
                                  borderLeftColor="#e8e8e8"
                                  alignItems="stretch"
                                >
                                  {/* Quantity Column */}
                                  <Box
                                    width={qtyWidth}
                                    alignItems="center"
                                    px={2}
                                    py={5}
                                    justifyContent="center"
                                    display="flex"
                                    bg={isBestPrice ? "#FAFFF9" : "white"}
                                  >
                                    <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color={quote ? "gray.800" : "#d0d0d0"} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                                      {quote ? displayQty : '—'}
                                    </Text>
                                  </Box>

                                  {/* Price Column */}
                                  <Box
                                    width={priceWidth}
                                    alignItems="center"
                                    px={4}
                                    py={5}
                                    justifyContent="center"
                                    display="flex"
                                    bg={isBestPrice ? "#FAFFF9" : "white"}
                                  >
                                    <HStack alignItems="center" space={2} justifyContent="center">
                                      {isBestPrice && (
                                        <Box
                                          w={4}
                                          h={4}
                                          borderRadius="full"
                                          bg="#16A34A"
                                          alignItems="center"
                                          justifyContent="center"
                                        >
                                          <Check size={13} color="white" />
                                        </Box>
                                      )}
                                      <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} color={quote ? "gray.800" : "#d0d0d0"} fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}>
                                        {quote ? formatCurrency(quote.unitPrice || 0) : '—'}
                                      </Text>
                                    </HStack>
                                  </Box>
                                  
                                  {/* Variance Column */}
                                  <Box
                                    width={varianceWidth}
                                    alignItems="center"
                                    px={4}
                                    py={5}
                                    justifyContent="center"
                                    display="flex"
                                    bg={isBestPrice ? "#FAFFF9" : "white"}
                                  >
                                    <Text
                                      fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                                      color={quote ? (quote.changePercent < 0 ? '#16A34A' : (quote.changePercent > 0 ? '#E85D35' : '#444')) : '#d0d0d0'}
                                      fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                    >
                                      {quote ? formatPercentage(quote.changePercent || 0) : '—'}
                                    </Text>
                                  </Box>
                                </HStack>
                              );
                            })}
                          </HStack>
                        );
                      })
                    ))
                  ))}
                </VStack>
              </Box>
            </Box>
          </Box>
        );
      })()}
    </VStack>
  );
};

export default QuotationAnalysisDashboardV2;
